import { inject, Injectable } from '@angular/core';
import { Observable, take } from 'rxjs';
import {
  ActionModalComponent,
  CacheKey,
  GameDetailsModel,
  GamePlayerModel,
  LOADING_TOKEN,
  LoadingProvider,
  LocalRefreshService,
  MODAL_TOKEN,
  ModalProvider,
  TOAST_TOKEN,
  ToastProvider,
  UserModel
} from '@core';
import {
  ApproveApplicationsCommandHandler,
  MarkAsPaidCommandHandler,
  RejectApplicationsCommandHandler
} from '../../../application/handlers';
import { ManagePlayerComponent } from '../../manage-player/manage-player.component';
import { ManageHostedGameComponent } from '../../manage-hosted-game/manage-hosted-game.component';
import { PaymentConfirmCheckboxComponent } from '../../payment-confirm-checkbox/payment-confirm-checkbox.component';
import { HIDE_PAYMENTS_MODAL } from '../../../application/utils';
import { OpenSingleChatCommandHandler } from '@messages';

@Injectable()
export class HostedGameDetailsViewService {
  private readonly modalProvider: ModalProvider = inject(MODAL_TOKEN);
  private readonly localRefreshService: LocalRefreshService = inject(LocalRefreshService);

  readonly refresh$: Observable<void> = this.localRefreshService.refresh$;
  private readonly loadingProvider: LoadingProvider = inject(LOADING_TOKEN);
  private readonly approveApplicationsCommandHandler: ApproveApplicationsCommandHandler = inject(
    ApproveApplicationsCommandHandler
  );
  private readonly rejectApplicationsCommandHandler: RejectApplicationsCommandHandler = inject(
    RejectApplicationsCommandHandler
  );
  private readonly markAsPaidCommandHandler: MarkAsPaidCommandHandler = inject(MarkAsPaidCommandHandler);
  private readonly openSingleChatCommandHandler: OpenSingleChatCommandHandler = inject(OpenSingleChatCommandHandler);
  private readonly toastProvider: ToastProvider = inject(TOAST_TOKEN);

  initializeLoading(): void {
    this.modalProvider.showLoading$();
  }

  acceptPlayer(gameId: string, gamePlayerApplicationId: string): void {
    this.loadingProvider.showLoading();

    this.approveApplicationsCommandHandler
      .approve(gameId, [gamePlayerApplicationId])
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.refresh();
          this.toastProvider.showToast({ message: 'Zaakceptowano prośbę gracza' });
        },
        error: () => this.loadingProvider.dismissLoading()
      });
  }

  rejectApplication(gameId: string, gamePlayerApplicationId: string): void {
    this.modalProvider.showModal$({
      component: ActionModalComponent,
      cssClass: 'present-modal',
      componentProps: {
        header: 'Odrzuć zgłoszenie',
        message: 'Ten gracz nie zostanie dodany do listy uczestników tej gierki.',
        btnOk: 'Odrzuć prośbę',
        btnCancel: 'Anuluj',
        action: () => this.executeRejectApplication(gameId, gamePlayerApplicationId)
      }
    });
  }

  managePlayer(gameId: string, player: GamePlayerModel, isPlayerInGame: boolean): void {
    this.modalProvider
      .showModal$({
        component: ManagePlayerComponent,
        componentProps: {
          gameId: gameId,
          player: player,
          isPlayerInGame: isPlayerInGame ?? true
        },
        cssClass: 'modal-auto',
        initialBreakpoint: 1,
        breakpoints: [0, 1]
      })
      .subscribe(() => this.refresh());
  }

  manageGame(game: GameDetailsModel | undefined, userData: UserModel | undefined): void {
    if (!game || !userData) return;

    this.modalProvider
      .showModal$({
        component: ManageHostedGameComponent,
        componentProps: {
          game: game,
          hostId: userData.userId
        },
        cssClass: 'modal-auto',
        initialBreakpoint: 1,
        breakpoints: [0, 1]
      })
      .subscribe(() => this.refresh());
  }

  sendMessage(userId: string, currentUrl: string, displayName?: string): void {
    if (!userId) return;

    sessionStorage.setItem(CacheKey.RedirectToGameFromChatUrl, currentUrl);
    this.openSingleChatCommandHandler.open(userId, displayName).subscribe();
  }

  changePaymentStatus(game: GameDetailsModel | undefined, userId: string, isPaid: boolean): void | Observable<unknown> {
    if (!game) return;

    const header: string[] = ['Oznacz jako zapłacone', 'Oznacz jako niezapłacone'];
    const message: string[] = [
      'Czy na pewno chcesz oznaczyć jako zapłacone?',
      'Czy na pewno chcesz oznaczyć jako niezapłacone?'
    ];

    const hideModal: { isHide: boolean } = JSON.parse(localStorage.getItem(HIDE_PAYMENTS_MODAL) as string);

    if (hideModal?.isHide) {
      this.modalProvider.dismissAllStoredModals();
      return this.markAsPaid(game.gameId, userId, isPaid);
    }

    return this.modalProvider.showModal$({
      component: ActionModalComponent,
      cssClass: 'present-modal',
      componentProps: {
        header: isPaid ? header[1] : header[0],
        message: isPaid ? message[1] : message[0],
        btnOk: 'Tak, potwierdzam',
        btnCancel: 'Anuluj',
        template: PaymentConfirmCheckboxComponent,
        action: () => this.markAsPaid(game.gameId, userId, isPaid)
      }
    });
  }

  refresh(): void {
    this.localRefreshService.emit();
  }

  private executeRejectApplication(gameId: string, gamePlayerApplicationId: string): void {
    this.loadingProvider.showLoading();
    this.rejectApplicationsCommandHandler
      .reject(gameId, [gamePlayerApplicationId])
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.refresh();
          this.toastProvider.showToast({ message: 'Odrzucono prośbę gracza' });
        },
        error: () => this.loadingProvider.dismissLoading()
      });
  }

  private markAsPaid(gameId: string, userId: string, isPaid: boolean): void {
    this.markAsPaidCommandHandler
      .markAsPaid(gameId, userId, isPaid)
      .pipe(take(1))
      .subscribe(() => {
        this.refresh();
        if (isPaid) {
          this.toastProvider.showToast({ message: 'Cofnięto oznaczenie wpłaty' });
        }
      });
  }
}
